import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState } from 'react'

const features = [
  {
    title: "Smart Dashboard",
    desc: "Track sales trends, revenue, and daily summaries at a glance.",
    icon: "/icons/Dashboard.png"
  },
  {
    title: "Cashier Mode",
    desc: "Fast checkout and receipt printing with intuitive design.",
    icon: "/icons/cashier2.png"
  },
  {
    title: "Inventory Tracker",
    desc: "Stock warnings, product movement, and restock predictions.",
    icon: "/icons/Inventory.png"
  },
  {
    title: "Sales Analytics",
    desc: "Daily, weekly, and monthly sales breakdowns with charts.",
    icon: "/icons/analytic.png"
  },
  {
    title: "Multi-User Access",
    desc: "Separate logins for Manager, Cashier, and Kitchen roles.\n*Future Update",
    icon: "/icons/multiuser1.png"
  },
];


const Landing = () => {
  const [isOpen, setIsOpen] = useState(false)
  const singlePlan = {
    name: "CashMate",
    price: "RM 130.00",
    tagline: "One plan with everything. Unlimited usage, all features included.",
    features: [
      "Unlimited users, cashiers, and branches",
      "Unlimited products, bundles, and variants",
      "All reports, exports, and backups included",
      "Customer display, KDS, and queue display",
      "API access and integrations",
      "Priority chat & email support",
    ],
  };
  return (
    <div className="relative min-h-screen w-full overflow-hidden text-white bg-[#0b0812]">

      {/* HERO */}
      <section className="relative h-screen w-full overflow-hidden bg-[#0b0812]">

        {/* Navbar (stays on top using absolute) */}
      <nav className="w-full absolute top-0 left-0 right-0 z-50 bg-transparent text-white px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/images/logo.png" alt="CashMate Logo" className="h-10 md:h-12" />
          <span style={{ fontFamily: 'Century Gothic' }} className="text-2xl tracking-[8px]">
            CASHMATE
          </span>
        </div>

        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-white/80">
          <a href="#overview" className="hover:text-white transition">Overview</a>
          <a href="/pricing" className="hover:text-white transition">Pricing</a>
          <a href="/release-note" className="hover:text-white transition">Release Note</a>
          <a href="https://cashmate.tawk.help" className="hover:text-white transition">Help</a>
          <a href="https://nctechnologyhub.com.my/contact" className="hover:text-white transition">Contact</a>
          <Link to="/login">
            <motion.button
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="px-5 py-2 text-sm font-semibold tracking-wide text-white bg-white/10 backdrop-blur-md rounded-full border border-white/20 hover:scale-105 transition"
            >
              Sign in
            </motion.button>
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <div className="md:hidden">
          <button onClick={() => setIsOpen(!isOpen)} className="focus:outline-none">
            <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {isOpen && (
          <div className="absolute top-20 left-0 right-0 bg-[#0b0812] shadow-lg text-center px-6 py-8 md:hidden z-40 space-y-6">
            <a href="#overview" className="block text-white/80 hover:text-white transition">Overview</a>
            <a href="/pricing" className="block text-white/80 hover:text-white transition">Pricing</a>
            <a href="/release-note" className="block text-white/80 hover:text-white transition">Release Note</a>
            <a href="https://cashmate.tawk.help" className="block text-white/80 hover:text-white transition">Help</a>
            <a href="https://nctechnologyhub.com.my/contact" className="block text-white/80 hover:text-white transition">Contact</a>
            <Link to="/login">
              <button className="mt-4 w-full px-6 py-2 bg-white/10 text-white border border-white/20 rounded-full hover:scale-105 transition">
                Sign in
              </button>
            </Link>
          </div>
        )}
      </nav>

        {/* Glow Background Transition Layers */}
        {["landing-glow1.png", "landing-glow2.png", "landing-glow3.png"].map((img, i) => (
          <motion.div
            key={i}
            className="absolute inset-0 bg-no-repeat bg-cover bg-center z-0"
            style={{ backgroundImage: `url('/images/${img}')` }}
            initial={{ opacity: i === 0 ? 1 : 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 1.2, duration: 1.2 }}
          />
        ))}

        {/* Hero Content */}
        <div className="relative z-10 pt-40 md:pt-20 flex flex-col items-center text-center px-4">
          <h1 className="text-3xl md:text-6xl font-bold mb-4">Introducing <br className="hidden md:block" /> CashMate</h1>
          <p className="text-base md:text-xl max-w-2xl text-white/80 mb-10">
            Streamline and manage every aspect of your business faster<br className="hidden sm:block" />
            and more efficiently than ever before.
          </p>
          <Link to="/signup">
            <motion.button
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 2.5, duration: 1.2 }}
              className="px-10 py-4 text-lg font-bold tracking-widest text-white bg-white/10 backdrop-blur-md rounded-full border border-white/20 hover:scale-105 transition"
            >
              Sign Up For Free
            </motion.button>
          </Link>
        </div>
      </section>

      {/* OVERVIEW */}
      <section id="overview" className="bg-[#0b0812] text-white py-32 px-6 md:px-20">
        {/* Intro */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Why <span className="text-[#a46bf5]">CashMate</span>?</h2>
          <p className="text-lg text-white/80 leading-relaxed">
            CashMate isn’t just a POS system, it’s your all-in-one business solution, built to empower small and growing businesses with intuitive, powerful tools.
            Effortlessly manage sales, inventory, and staff from a single dashboard, and get instant insights with real-time reports and analytics. 
            CashMate integrates seamlessly with e-invoicing, QR payments, and digital receipts, keeping your business modern and efficient. 
            Advanced inventory features help you track stock, set reorder alerts, and manage suppliers easily, while flexible user controls and customizable receipts keep your operations secure and professional. 
            Whether you run a café, retail shop, or restaurant, CashMate adapts to your needs with table management, kitchen display, and queue systems. 
            With cloud backup, multi-device access, local support, and continuous updates. All at an affordable price. CashMate lets you focus on what matters most, which is growing your business.
          </p>
        </div>

        {/* Pricing */}
        <div id="pricing" className="mt-32">
            <div className="text-center mb-16">
              <span className="inline-block mb-4 px-4 py-1.5 rounded-full bg-[#a46bf51a] text-[#a46bf5] text-sm font-medium">
                Pricing
              </span>
              <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-[#c7b2ff]">
                One plan. All features.
              </h2>
              <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
                RM130/month • 7-day free trial • Unlimited everything. No yearly tiers, no hidden limits.
              </p>
            </div>

            <div className="relative max-w-5xl mx-auto">
              {/* Background elements */}
              <div className="absolute -top-20 -left-20 w-64 h-64 bg-[#a46bf5] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
              <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-[#6c5ce7] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
              
              <motion.div
                className="relative rounded-3xl overflow-hidden border-2 border-[#a46bf5] bg-gradient-to-b from-[#1a102b] to-[#0f0a1d] z-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="p-10 md:p-12 space-y-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex flex-col items-start gap-2">
                      <span className="text-sm uppercase tracking-[4px] text-purple-300 font-semibold">{singlePlan.name}</span>
                      <div className="flex items-baseline gap-2">
                        <span className="text-5xl font-bold text-white">{singlePlan.price}</span>
                        <span className="text-sm text-white/60">/ month</span>
                      </div>
                      <span className="text-sm text-white/70">{singlePlan.tagline}</span>
                    </div>

                    <Link to="/signup">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="px-8 py-3 rounded-2xl text-sm font-semibold bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/30"
                      >
                        Start Free Trial
                      </motion.button>
                    </Link>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    {singlePlan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-white/90">
                        <svg className="w-4 h-4 text-[#a46bf5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-4 text-xs text-white/80">
                    <span className="px-3 py-1 rounded-full bg-white/10 border border-white/10">7-day free trial</span>
                    <span className="px-3 py-1 rounded-full bg-white/10 border border-white/10">No credit card required</span>
                    <span className="px-3 py-1 rounded-full bg-white/10 border border-white/10">Cancel anytime</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

        {/* Features */}
        <div className="mt-32">
          <div className="text-center mb-16">
            <span className="inline-block mb-4 px-4 py-1.5 rounded-full bg-[#a46bf51a] text-[#a46bf5] text-sm font-medium">
              Features
            </span>
            <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-[#c7b2ff]">
              Powerful <span className="text-[#a46bf5]">Features</span>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              Everything you need to run your business efficiently, all in one place
            </p>
          </div>
            
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div 
                key={index} 
                className="group relative bg-gradient-to-br from-[#1a102b] to-[#1a0d3a] border border-white/5 rounded-2xl p-6 backdrop-blur-sm hover:border-[#a46bf5]/30 transition-all duration-300 hover:-translate-y-1"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#a46bf510] to-transparent opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-300" />
                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-xl bg-[#a46bf510] flex items-center justify-center mb-6 group-hover:bg-[#a46bf520] transition-colors">
                    <img src={feature.icon} alt={feature.title} className="w-7 h-7 object-contain" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {feature.desc.split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0517] to-[#1a0d3a]" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJwYXR0ZXJuIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHBhdHRlcm5UcmFuc2Zvcm09InJvdGF0ZSg0NSkiPjxyZWN0IHg9IjAiIHk9IjAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNwYXR0ZXJuKSIvPjwvc3ZnPg==')]" />
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-20">
            <span className="inline-block mb-4 px-4 py-1.5 rounded-full bg-[#a46bf51a] text-[#a46bf5] text-sm font-medium">
              Get Started
            </span>
            <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-[#c7b2ff]">
              Get started in <span className="text-[#a46bf5]">minutes</span>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              Join thousands of businesses that trust CashMate to power their operations
            </p>
          </div>

          <div className="relative">
            {/* Connecting line */}
            <div className="hidden lg:block absolute left-1/2 top-0 h-full w-0.5 bg-gradient-to-b from-[#a46bf5] via-[#6c5ce7] to-[#a46bf5]" />
            
            <div className="space-y-16 lg:space-y-24">
              {[
                { 
                  number: '01', 
                  title: 'Sign Up', 
                  description: 'Create your free account in minutes with just your email',
                  color: '#a46bf5',
                  reverse: false
                },
                { 
                  number: '02', 
                  title: 'Set Up', 
                  description: 'Add your products, menu items, and staff members',
                  color: '#6c5ce7',
                  reverse: true
                },
                { 
                  number: '03', 
                  title: 'Sell', 
                  description: 'Start processing orders and managing your business',
                  color: '#f472b6',
                  reverse: false
                },
                { 
                  number: '04', 
                  title: 'Grow', 
                  description: 'Use insights and analytics to grow your business',
                  color: '#facc15',
                  reverse: true
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  className={`flex flex-col lg:flex-row items-center ${step.reverse ? 'lg:flex-row-reverse' : ''} gap-8 lg:gap-16`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  {/* Step number */}
                  <div className="relative z-10 flex-shrink-0 w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold text-white"
                    style={{
                      background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}80 100%)`,
                      boxShadow: `0 0 0 10px ${step.color}20`
                    }}
                  >
                    {step.number}
                  </div>
                  
                  {/* Step content */}
                  <div className={`flex-1 ${step.reverse ? 'lg:text-right' : 'lg:text-left'} text-center lg:text-left`}>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">{step.title}</h3>
                    <p className="text-white/70 text- mx-auto lg:mx-0">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

                      {/* CTA Dual Chart */}
                      <section className="bg-[#0b0812] text-white px-6 py-20">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 items-center gap-10">
          <div className="space-y-6 text-center md:text-left">
            <h2 className="text-3xl md:text-5xl font-bold leading-tight">
              <span className="text-[#a46bf5]">Automate</span> your business with <span className="text-[#a46bf5]">us</span>.
            </h2>
            <Link to="/signup">
              <button className="mt-4 inline-flex items-center gap-2 bg-white text-[#3F51F5] font-semibold px-6 py-3 rounded-full shadow hover:bg-gray-100 transition">
                Try for Free →
              </button>
            </Link>
          </div>
          <div className="relative w-full flex justify-center md:justify-end">
            <img src="/images/dashboard.png" alt="Monthly Revenue" className="w-72 md:w-80 rounded-xl shadow-lg" />
            <img src="/images/cashier.png" alt="Top Expenses" className="absolute top-[-35px] left-0 w-40 md:w-48 rounded-xl shadow-xl" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
